import { CO_REBATE_WINDOWS } from '@/lib/constants'

export type RebateWindowState =
  | { status: 'open'; opens: Date; closes: Date; daysUntil: 0 }
  | { status: 'upcoming'; opens: Date; closes: Date; daysUntil: number }
  | { status: 'none'; opens: null; closes: null; daysUntil: null }

/**
 * Resolve the Colorado e-bike rebate registration window relative to `now`.
 *
 * The state publishes fixed registration windows (see CO_REBATE_WINDOWS).
 * Deriving the state at render time means the page is never wrong about
 * whether registration is open — nobody has to remember to edit copy on
 * August 24 or August 31.
 *
 * Returns 'none' once every published window is in the past, which is the
 * signal that CO_REBATE_WINDOWS needs new dates from the Energy Office.
 */
export function resolveRebateWindow(now: Date = new Date()): RebateWindowState {
  const t = now.getTime()

  for (const w of CO_REBATE_WINDOWS) {
    const opens = new Date(w.opens)
    const closes = new Date(w.closes)

    if (t >= opens.getTime() && t <= closes.getTime()) {
      return { status: 'open', opens, closes, daysUntil: 0 }
    }

    if (t < opens.getTime()) {
      const msPerDay = 24 * 60 * 60 * 1000
      return {
        status: 'upcoming',
        opens,
        closes,
        daysUntil: Math.ceil((opens.getTime() - t) / msPerDay),
      }
    }
  }

  return { status: 'none', opens: null, closes: null, daysUntil: null }
}

/** "August 24" — the format used in the rebate banner and page copy. */
export function formatWindowDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'America/Denver',
  })
}
